import {
  CalendarDays,
  Camera,
  Compass,
  Map,
  Sparkles,
  UsersRound,
  Eye,
  EyeOff,
  LogOut,
} from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { sessionStore } from '../../features/access/sessionStore'
import { caravanDb } from '../../services/syncRuntime'
import { InstallCaravanButton } from '../../features/install/PwaProvider'
import { Button, Sheet } from '../ui'
import { useActionableTabs } from '../../features/navigation/useActionableTabs'

const destinations = [
  { to: '/', label: 'Aujourd’hui', icon: Sparkles, end: true },
  { to: '/agenda', label: 'Agenda', icon: CalendarDays },
  { to: '/carte', label: 'Carte', icon: Map },
  { to: '/groupe', label: 'Groupe', icon: UsersRound },
  { to: '/souvenirs', label: 'Souvenirs', icon: Camera },
  { to: '/pratique', label: 'Pratique', icon: Compass },
] as const

export function AppShell() {
  const { pathname } = useLocation()
  const activeIndex = Math.max(0, destinations.findIndex(({ to }) => to === '/' ? pathname === '/' : pathname.startsWith(to)))
  const previousIndex = useRef(activeIndex)
  const transitionNode = useRef<HTMLDivElement | null>(null)
  const session = sessionStore((state) => state.session)
  const participantPreview = sessionStore((state) => state.participantPreview)
  const actionableTabs = useActionableTabs()
  const [accountOpen, setAccountOpen] = useState(false)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [pathname])
  useLayoutEffect(() => {
    const direction = activeIndex >= previousIndex.current ? 'forward' : 'backward'
    transitionNode.current?.classList.add(`route-transition--${direction}`)
    previousIndex.current = activeIndex
  }, [activeIndex, pathname])
  useEffect(() => {
    if (!session) return
    void caravanDb.tripArchives.get(session.tripId).then((current) => {
      if (current?.status === 'archived' || (current && session.status === 'active')) return
      return caravanDb.tripArchives.put({
        tripId: session.tripId,
        status: session.status === 'archived_read_only' ? 'archived' : 'active',
        archivedAt: session.status === 'archived_read_only' ? new Date().toISOString() : null,
        version: session.status === 'archived_read_only' ? 2 : 1,
      })
    })
  }, [session])

  return (
    <div className="shell">
      <header className="shell__brand" aria-label="Caravan">
        <img className="shell__wordmark" src="/brand/caravan-wordmark.svg" alt="" />
        <img className="shell__compact-mark" src="/brand/caravan-monogram.svg" alt="" />
        <InstallCaravanButton compact />
        {session ? (
          <button
            aria-label={`Ouvrir le menu de ${session.displayName}`}
            className="shell__account-trigger"
            type="button"
            onClick={() => setAccountOpen(true)}
          >
            <span aria-hidden="true">{session.displayName.slice(0, 1)}</span>
            <strong>{session.displayName}</strong>
          </button>
        ) : null}
      </header>
      <div className="shell__content">
        <div className="route-transition" key={pathname} ref={transitionNode}>
          <Outlet />
        </div>
      </div>
      <nav className="tab-bar" aria-label="Navigation principale">
        {destinations.map(({ to, label, icon: Icon, ...linkProps }) => (
          <NavLink
            {...linkProps}
            className={({ isActive }) => `tab-bar__item${isActive ? ' is-active' : ''}`}
            key={to}
            to={to}
          >
            <Icon size={20} strokeWidth={1.8} aria-hidden="true" />
            <span>{label}</span>
            {actionableTabs.has(to) ? <i className="tab-bar__dot" aria-hidden="true" /> : null}
          </NavLink>
        ))}
      </nav>
      {accountOpen && session ? (
        <Sheet title="Compte" onClose={() => setAccountOpen(false)}>
          <div className="account-sheet">
            <span className="account-sheet__avatar" aria-hidden="true">{session.displayName.slice(0, 1)}</span>
            <div><p className="eyebrow">CONNECTÉ SUR CET APPAREIL</p><h3>{session.displayName}</h3></div>
            {session.role === 'admin' ? <Button aria-pressed={participantPreview} variant="ghost" onClick={() => {
              sessionStore.getState().setParticipantPreview(!participantPreview)
              setAccountOpen(false)
            }}>
              {participantPreview ? <EyeOff size={18} /> : <Eye size={18} />}
              {participantPreview ? 'Revenir aux outils de Nicolas' : 'Voir comme un participant'}
            </Button> : null}
            <Button variant="secondary" onClick={() => sessionStore.getState().clearSession()}><LogOut size={18} /> Se déconnecter</Button>
          </div>
        </Sheet>
      ) : null}
    </div>
  )
}
